// =============================================================================
// ROLE SYSTEM (aligned with database schema)
// =============================================================================

/**
 * Entity types in the platform hierarchy
 * - system: Platform-wide scope (no entity_id)
 * - integrator: White-label partner scope (entity_id = integrator UUID)
 * - organization: End-customer scope (entity_id = organization UUID)
 */
export type EntityType = "system" | "integrator" | "organization";

/**
 * Role levels within each entity type
 * Matches database constraint: CHECK (role IN ('owner', 'admin', 'editor', 'viewer'))
 */
export type RoleLevel = "owner" | "admin" | "editor" | "viewer";

/**
 * Display names for role levels
 */
export const ROLE_LEVEL_DISPLAY_NAMES: Record<RoleLevel, string> = {
  owner: "Owner",
  admin: "Administrator",
  editor: "Editor",
  viewer: "Viewer",
};

/**
 * Display names for entity types
 */
export const ENTITY_TYPE_DISPLAY_NAMES: Record<EntityType, string> = {
  system: "System",
  integrator: "Integrator",
  organization: "Organization",
};

/**
 * Get full role display name combining entity type and role level
 * e.g., "System Owner", "Integrator Admin", "Organization Viewer"
 */
export const getFullRoleDisplayName = (
  entityType?: EntityType | string,
  role?: RoleLevel | string
): string => {
  if (!entityType || !role) return "Unknown Role";
  const entityDisplay = ENTITY_TYPE_DISPLAY_NAMES[entityType as EntityType] || entityType;
  const roleDisplay = ROLE_LEVEL_DISPLAY_NAMES[role as RoleLevel] || role;
  return `${entityDisplay} ${roleDisplay}`;
};

/**
 * Get badge variant for role display
 */
export const getRoleBadgeVariant = (
  entityType?: EntityType | string,
  role?: RoleLevel | string
): "default" | "secondary" | "destructive" | "outline" => {
  if (entityType === "system") {
    return role === "owner" ? "destructive" : "default";
  }
  if (entityType === "integrator") {
    return role === "owner" || role === "admin" ? "default" : "secondary";
  }
  // organization
  return role === "owner" || role === "admin" ? "secondary" : "outline";
};

// =============================================================================
// LEGACY ROLE TYPES (for backward compatibility)
// =============================================================================

/**
 * @deprecated Use RoleLevel and EntityType instead
 * Legacy role types kept for backward compatibility during migration
 */
export enum UserRoleType {
  SYSTEM_ADMIN = "SYSTEM_ADMIN",
  INTEGRATOR_ADMIN = "INTEGRATOR_ADMIN",
  INTEGRATOR_USER = "INTEGRATOR_USER",
  ORGANIZATION_ADMIN = "ORGANIZATION_ADMIN",
  ORGANIZATION_USER = "ORGANIZATION_USER",
}

/** @deprecated Use getFullRoleDisplayName instead */
export const USER_ROLE_DISPLAY_NAMES: Record<UserRoleType, string> = {
  [UserRoleType.SYSTEM_ADMIN]: "System Admin",
  [UserRoleType.INTEGRATOR_ADMIN]: "Integrator Admin",
  [UserRoleType.INTEGRATOR_USER]: "Integrator User",
  [UserRoleType.ORGANIZATION_ADMIN]: "Organization Admin",
  [UserRoleType.ORGANIZATION_USER]: "Organization User",
};

/** @deprecated Use getFullRoleDisplayName instead */
export const getUserRoleDisplayName = (role: string): string => {
  return USER_ROLE_DISPLAY_NAMES[role as UserRoleType] || role;
};

/** @deprecated Use getRoleBadgeVariant instead */
export const getUserRoleBadgeVariant = (
  role: string
): "default" | "secondary" | "destructive" | "outline" => {
  switch (role as UserRoleType) {
    case UserRoleType.SYSTEM_ADMIN:
      return "destructive";
    case UserRoleType.INTEGRATOR_ADMIN:
      return "default";
    case UserRoleType.INTEGRATOR_USER:
      return "outline";
    case UserRoleType.ORGANIZATION_ADMIN:
      return "secondary";
    case UserRoleType.ORGANIZATION_USER:
      return "outline";
    default:
      return "outline";
  }
};

/** @deprecated */
export const getAuthorityDisplayName = getUserRoleDisplayName;
/** @deprecated */
export const getAuthorityBadgeVariant = getUserRoleBadgeVariant;
